import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { useTranslation } from "react-i18next";
import { IAxiosGetResponse, IClaimAutocompleteResultDto } from "../../../../interfaces/IUtilities";
import { post } from "../../../../pages/_request";
import { useToast } from "../ToastContext";
import { getAllClaims, mapAllClaims } from "../../../../helpers/ClaimBuilderHelper";


interface ClaimTypeaheadProps {
  id: string;
  defaultValue: string;
  showError: boolean;
  onClaimChanged: (claim: IClaimAutocompleteResultDto) => void;
  formik: any;
  onClaimMapUpdate?: (map: Record<string, string>) => void; 
}

const ClaimTypeahead = forwardRef<{
    clear: () => void;
  }, ClaimTypeaheadProps>(({ id, defaultValue, showError, onClaimChanged, formik, onClaimMapUpdate 
  }, ref) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [allResults, setAllResults] = useState([] as IClaimAutocompleteResultDto[]);
  const [searchResults, setSearchResults] = useState([] as IClaimAutocompleteResultDto[]);
  const { showToast } = useToast();
  const [selected, setSelected] = useState<IClaimAutocompleteResultDto | null>(null);
  const typeaheadRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    clear: () => {
      typeaheadRef.current?.clear();
    }
  }));
  
  const fetchData = async () => {
    try {
      setAllResults(await getAllClaims());
    } catch (error) {
      showToast(t('ClaimSearchFailed', { error: String(error) }), 'error');
    }
  };

  const handleSearch = async (query: string) => {
    setLoading(true);
    try {
      let result = await post(`/api/claims/autocomplete/?searchText=${query}`, {}) as IAxiosGetResponse;
      var respStatus = result?.response.status;
      if(respStatus === 200)
      {
        setLoading(false);
        setSearchResults(result.data as IClaimAutocompleteResultDto[]);
      }
      else {
          setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      showToast(t('ClaimSearchFailed', { error: String(error) }), 'error');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);


  useEffect(() => {
    const map = mapAllClaims(allResults);

    onClaimMapUpdate?.(map); //Send up to ClaimBuilder
    
    const match = allResults.find((opt) => opt.id === formik.values.claimId);
    if (match) setSelected(match);
    else setSelected(null);
  }, [allResults, onClaimMapUpdate, formik.values.claimId]);


  return (
    <>
      <AsyncTypeahead
        id="claim-typeahead"
        key={id + formik.values.claimId} 
        labelKey={(option) => `${(option as IClaimAutocompleteResultDto).type}`}
        selected={selected ? [selected] : []}
        delay={200}
        minLength={2}
        isLoading={loading}
        className={`${showError ? 'is-invalid' : ''}`}
        onSearch={handleSearch}
        options={searchResults}
        placeholder={t('PleaseSelectClaim')}
        inputProps={{
          className: `form-control ${formik.touched.claimId && formik.errors.claimId ? 'is-invalid' : ''}`,
        }}
        ref={typeaheadRef}
        filterBy={() => true}
        onChange={(selectedOptions) => {
          if (selectedOptions.length > 0) {
            const selectedClaim = selectedOptions[0] as IClaimAutocompleteResultDto;
            formik.setFieldValue('claimId', selectedClaim.id);
            onClaimChanged(selectedClaim);
            setSelected(selectedClaim);
          } else {
            formik.setFieldValue('claimId', '');
            onClaimChanged({ id: '', type: '', value: '' });
            setSelected(null);
            typeaheadRef.current?.clear(); //Clear the typeahead input
          }
        }}
        renderMenuItemChildren={(option) => {
          const claim = option as IClaimAutocompleteResultDto;
          return (
            <div>
              <strong>{claim.type}</strong>
            </div>
          );
        }}
        onBlur={(e) => {
          const matched = allResults.find(opt => `${opt.type}` === e.target.value);
          if (!matched) {
            formik.setFieldValue('claimId', '');
            onClaimChanged({ id: '', type: '', value: '' });
            setSelected(null);
            typeaheadRef.current?.clear(); //Clear input here too
          }
        }}
      />
    </>
  );
});

export default ClaimTypeahead;
